import { ClipPolygon } from "./clipMask";
import {
  DEFAULT_BLOCK_GAP_KM,
  DEFAULT_RBF_SMOOTHING,
  STATION_BUFFER_KM,
  VALUE_WITHOUT_STATION_MM,
} from "./constants";
import { projectToUtm21S } from "./projection";
import {
  buildThinPlateSpline,
  evaluateThinPlateSplineAtKm,
  gaussianBlurValues,
  interpolateProjectedIdwFromMeters,
  ThinPlateSplineModel,
} from "./rbf";
import { yieldToMain } from "./schedule";
import {
  buildRainfallSurfaceBlocks,
  rasterizeBlockIndexes,
} from "./surfaceBlocks";
import { GeoBounds, RainfallStation } from "./types";

interface RasterBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const ROWS_PER_YIELD = 24;

function findBlockRasterBounds(
  blockIndexes: Int16Array,
  blockIndex: number,
  width: number,
  height: number,
  padding: number,
): RasterBounds | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let index = 0; index < blockIndexes.length; index += 1) {
    if (blockIndexes[index] !== blockIndex) {
      continue;
    }
    const x = index % width;
    const y = Math.floor(index / width);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  if (maxX < minX || maxY < minY) {
    return null;
  }
  return {
    minX: Math.max(0, minX - padding),
    minY: Math.max(0, minY - padding),
    maxX: Math.min(width - 1, maxX + padding),
    maxY: Math.min(height - 1, maxY + padding),
  };
}

async function fillBlockValues(options: {
  blockIndexes: Int16Array;
  blockIndex: number;
  localStations: RainfallStation[];
  bounds: GeoBounds;
  width: number;
  height: number;
  idwPower: number;
  smoothingSigma: number;
  output: Float32Array;
  shouldCancel?: () => boolean;
}): Promise<void> {
  const {
    blockIndexes,
    blockIndex,
    localStations,
    bounds,
    width,
    height,
    idwPower,
    smoothingSigma,
    output,
    shouldCancel,
  } = options;

  const padding = Math.ceil(Math.max(0, smoothingSigma) * 3);
  const rasterBounds = findBlockRasterBounds(
    blockIndexes,
    blockIndex,
    width,
    height,
    padding,
  );
  if (!rasterBounds) {
    return;
  }

  const localWidth = rasterBounds.maxX - rasterBounds.minX + 1;
  const localHeight = rasterBounds.maxY - rasterBounds.minY + 1;
  const minimum = localStations.length
    ? Math.min(...localStations.map((station) => station.precipitacaoMm))
    : VALUE_WITHOUT_STATION_MM;
  const maximum = localStations.length
    ? Math.max(...localStations.map((station) => station.precipitacaoMm))
    : VALUE_WITHOUT_STATION_MM;
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;
  const model =
    localStations.length > 0 && localStations.length <= 16
      ? buildThinPlateSpline(localStations, DEFAULT_RBF_SMOOTHING)
      : null;
  const neighborhoodModels = new Map<string, ThinPlateSplineModel | null>();
  const projectedStations = localStations.map((station) => {
    const projected = projectToUtm21S(station.lat, station.lng);
    return {
      x: projected.x,
      y: projected.y,
      value: station.precipitacaoMm,
    };
  });

  const resolveModel = (
    queryX: number,
    queryY: number,
  ): ThinPlateSplineModel | null => {
    if (localStations.length <= 16) {
      return model;
    }
    const nearestIndexes = projectedStations
      .map((station, index) => ({
        index,
        distance:
          Math.pow(station.x - queryX, 2) + Math.pow(station.y - queryY, 2),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 16)
      .map((item) => item.index)
      .sort((a, b) => a - b);
    const key = nearestIndexes.join(",");
    if (!neighborhoodModels.has(key)) {
      neighborhoodModels.set(
        key,
        buildThinPlateSpline(
          nearestIndexes.map((index) => localStations[index]),
          DEFAULT_RBF_SMOOTHING,
        ),
      );
    }
    return neighborhoodModels.get(key) ?? null;
  };

  const localValues = new Float32Array(localWidth * localHeight);

  for (let localY = 0; localY < localHeight; localY += 1) {
    if (shouldCancel?.()) {
      return;
    }
    if (localY > 0 && localY % ROWS_PER_YIELD === 0) {
      await yieldToMain();
      if (shouldCancel?.()) {
        return;
      }
    }

    const globalY = rasterBounds.minY + localY;
    const lat = bounds.north - ((globalY + 0.5) / height) * latSpan;
    for (let localX = 0; localX < localWidth; localX += 1) {
      const globalX = rasterBounds.minX + localX;
      const lng = bounds.west + ((globalX + 0.5) / width) * lngSpan;
      let value = minimum;
      if (maximum - minimum > 1e-9) {
        const projected = projectToUtm21S(lat, lng);
        const localModel = resolveModel(projected.x, projected.y);
        if (localModel) {
          value = evaluateThinPlateSplineAtKm(
            localModel,
            (projected.x - localModel.originX) / 1000,
            (projected.y - localModel.originY) / 1000,
          );
        } else {
          value = interpolateProjectedIdwFromMeters(
            projectedStations,
            projected.x,
            projected.y,
            idwPower,
          );
        }
      }
      localValues[localY * localWidth + localX] = Math.max(
        minimum,
        Math.min(maximum, value),
      );
    }
  }

  if (shouldCancel?.()) {
    return;
  }

  const smoothed = gaussianBlurValues(
    localValues,
    localWidth,
    localHeight,
    smoothingSigma,
  );

  for (let localY = 0; localY < localHeight; localY += 1) {
    const globalY = rasterBounds.minY + localY;
    for (let localX = 0; localX < localWidth; localX += 1) {
      const globalX = rasterBounds.minX + localX;
      const globalIndex = globalY * width + globalX;
      if (blockIndexes[globalIndex] === blockIndex) {
        output[globalIndex] = Math.max(
          minimum,
          Math.min(maximum, smoothed[localY * localWidth + localX]),
        );
      }
    }
  }
}

export async function buildCurvedRainfallValuesAsync(options: {
  stations: RainfallStation[];
  polygons: ClipPolygon[];
  bounds: GeoBounds;
  width: number;
  height: number;
  idwPower: number;
  smoothingSigma: number;
  shouldCancel?: () => boolean;
}): Promise<Float32Array | null> {
  const {
    stations,
    polygons,
    bounds,
    width,
    height,
    idwPower,
    smoothingSigma,
    shouldCancel,
  } = options;

  const output = new Float32Array(width * height);
  output.fill(Number.NaN);
  const blocks = buildRainfallSurfaceBlocks(
    polygons,
    stations,
    bounds,
    DEFAULT_BLOCK_GAP_KM,
    STATION_BUFFER_KM,
  );
  const blockIndexes = polygons.length
    ? rasterizeBlockIndexes(polygons, blocks, bounds, width, height)
    : new Int16Array(width * height);

  for (let blockIndex = 0; blockIndex < blocks.length; blockIndex += 1) {
    if (shouldCancel?.()) {
      return null;
    }
    await fillBlockValues({
      blockIndexes,
      blockIndex,
      localStations: blocks[blockIndex].stations,
      bounds,
      width,
      height,
      idwPower,
      smoothingSigma,
      output,
      shouldCancel,
    });
    await yieldToMain();
  }

  return shouldCancel?.() ? null : output;
}

export function buildCurvedRainfallValues(options: {
  stations: RainfallStation[];
  polygons: ClipPolygon[];
  bounds: GeoBounds;
  width: number;
  height: number;
  idwPower: number;
  smoothingSigma: number;
}): Float32Array {
  const {
    stations,
    polygons,
    bounds,
    width,
    height,
    idwPower,
    smoothingSigma,
  } = options;
  const output = new Float32Array(width * height);
  output.fill(Number.NaN);
  const blocks = buildRainfallSurfaceBlocks(
    polygons,
    stations,
    bounds,
    DEFAULT_BLOCK_GAP_KM,
    STATION_BUFFER_KM,
  );
  const blockIndexes = polygons.length
    ? rasterizeBlockIndexes(polygons, blocks, bounds, width, height)
    : new Int16Array(width * height);
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;
  const padding = Math.ceil(Math.max(0, smoothingSigma) * 3);

  blocks.forEach((block, blockIndex) => {
    const rasterBounds = findBlockRasterBounds(
      blockIndexes,
      blockIndex,
      width,
      height,
      padding,
    );
    if (!rasterBounds) {
      return;
    }

    const localWidth = rasterBounds.maxX - rasterBounds.minX + 1;
    const localHeight = rasterBounds.maxY - rasterBounds.minY + 1;
    const localStations = block.stations;
    const minimum = localStations.length
      ? Math.min(...localStations.map((station) => station.precipitacaoMm))
      : VALUE_WITHOUT_STATION_MM;
    const maximum = localStations.length
      ? Math.max(...localStations.map((station) => station.precipitacaoMm))
      : VALUE_WITHOUT_STATION_MM;
    const model =
      localStations.length > 0 && localStations.length <= 16
        ? buildThinPlateSpline(localStations, DEFAULT_RBF_SMOOTHING)
        : null;
    const neighborhoodModels = new Map<string, ThinPlateSplineModel | null>();
    const projectedStations = localStations.map((station) => {
      const projected = projectToUtm21S(station.lat, station.lng);
      return {
        x: projected.x,
        y: projected.y,
        value: station.precipitacaoMm,
      };
    });
    const resolveModel = (
      queryX: number,
      queryY: number,
    ): ThinPlateSplineModel | null => {
      if (localStations.length <= 16) {
        return model;
      }
      const nearestIndexes = projectedStations
        .map((station, index) => ({
          index,
          distance:
            Math.pow(station.x - queryX, 2) + Math.pow(station.y - queryY, 2),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 16)
        .map((item) => item.index)
        .sort((a, b) => a - b);
      const key = nearestIndexes.join(",");
      if (!neighborhoodModels.has(key)) {
        neighborhoodModels.set(
          key,
          buildThinPlateSpline(
            nearestIndexes.map((index) => localStations[index]),
            DEFAULT_RBF_SMOOTHING,
          ),
        );
      }
      return neighborhoodModels.get(key) ?? null;
    };
    const localValues = new Float32Array(localWidth * localHeight);

    for (let localY = 0; localY < localHeight; localY += 1) {
      const globalY = rasterBounds.minY + localY;
      const lat = bounds.north - ((globalY + 0.5) / height) * latSpan;
      for (let localX = 0; localX < localWidth; localX += 1) {
        const globalX = rasterBounds.minX + localX;
        const lng = bounds.west + ((globalX + 0.5) / width) * lngSpan;
        let value = minimum;
        if (maximum - minimum > 1e-9) {
          const projected = projectToUtm21S(lat, lng);
          const localModel = resolveModel(projected.x, projected.y);
          if (localModel) {
            value = evaluateThinPlateSplineAtKm(
              localModel,
              (projected.x - localModel.originX) / 1000,
              (projected.y - localModel.originY) / 1000,
            );
          } else {
            value = interpolateProjectedIdwFromMeters(
              projectedStations,
              projected.x,
              projected.y,
              idwPower,
            );
          }
        }
        localValues[localY * localWidth + localX] = Math.max(
          minimum,
          Math.min(maximum, value),
        );
      }
    }

    const smoothed = gaussianBlurValues(
      localValues,
      localWidth,
      localHeight,
      smoothingSigma,
    );
    for (let localY = 0; localY < localHeight; localY += 1) {
      const globalY = rasterBounds.minY + localY;
      for (let localX = 0; localX < localWidth; localX += 1) {
        const globalX = rasterBounds.minX + localX;
        const globalIndex = globalY * width + globalX;
        if (blockIndexes[globalIndex] === blockIndex) {
          output[globalIndex] = Math.max(
            minimum,
            Math.min(maximum, smoothed[localY * localWidth + localX]),
          );
        }
      }
    }
  });

  return output;
}
